import { m } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// hooks
import { useMockedUser } from 'src/hooks/use-mocked-user';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useBoolean } from 'src/hooks/use-boolean';
import { LoadingButton } from '@mui/lab';
import { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

const OPTIONS = [
  // {
  //   label: 'Home',
  //   linkTo: '/',
  // },
  {
    label: 'Profile',
    linkTo: paths.dashboard.user.new,
  },
  // {
  //   label: 'Settings',
  //   linkTo: paths.dashboard.user.account,
  // },
];

// ----------------------------------------------------------------------

// Helper function to get label based on URL
const getBackLabel = (url) => {
  if (!url) return null;

  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('portal')) {
    return 'Back to Portal';
  }
  if (lowerUrl.includes('sales')) {
    return 'Back to Map';
  }
  if (lowerUrl.includes('canvasser')) {
    return 'Back to Canvasser';
  }

  return 'Back';
};

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();

  const { logout, user } = useAuthContext();
  // const data = useMockedUser();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [redirectingFrom, setRedirectingFrom] = useState(null);

  const popover = usePopover();

  const confirm = useBoolean();

  // Read redirectingFrom from localStorage
  useEffect(() => {
    const redirectLink = localStorage.getItem('redirectingFrom');
    setRedirectingFrom(redirectLink);
  }, []);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      setLoading(false);
      popover.onClose();
      sessionStorage.removeItem('courseSectionsView');
      sessionStorage.removeItem('view');
      router.replace('/');
    } catch (error) {
      setLoading(false);
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = (path) => {
    popover.onClose();
    router.push(path);
  };

  const handleBackClick = () => {
    if (redirectingFrom) {
      popover.onClose();
      window.open(redirectingFrom, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={user?.companyLogoUrl || ''}
          alt={user?.userName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user
              ? (user.firstName || user.lastName
                ? `${user.firstName ? user.firstName + ' ' + user.lastName : ''}`
                : user.userName || '')
              : ''}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {redirectingFrom && (
            <MenuItem onClick={handleBackClick}>
              {getBackLabel(redirectingFrom)}
            </MenuItem>
          )}
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={confirm.onTrue}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title="Logout"
          content="Are you sure want to Logout?"
          action={
            <LoadingButton
              loading={loading}
              variant="contained"
              color="error"
              onClick={handleLogout}
            >
              Logout
            </LoadingButton>
          }
        />
      </CustomPopover>
    </>
  );
}